<template>
  <section class="page-shell approvals-page">
    <div class="page-heading">
      <div>
        <h1>Revisões humanas</h1>
        <p>Fila de exceções: documentos que precisam de uma decisão responsável antes de chegar ao cliente, tribunal ou terceiro.</p>
      </div>
      <v-btn color="primary" @click="openDialog">
        <v-icon left small>mdi-plus</v-icon>
        Enviar documento
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <section class="approval-focus-panel">
      <article>
        <span>Para decidir agora</span>
        <strong>{{ pendingCount }}</strong>
        <small>Documentos aguardando jurídico, DPO ou segurança</small>
      </article>
      <article>
        <span>Entrada ideal</span>
        <strong>Validação</strong>
        <small>A validação identifica risco e envia para esta fila quando houver exceção</small>
      </article>
      <article>
        <span>Saída esperada</span>
        <strong>Evidência</strong>
        <small>Cada decisão registra responsável, motivo e versão revisada</small>
      </article>
    </section>

    <section class="human-review-purpose">
      <div>
        <span>O que o humano revisa?</span>
        <h2>Não é burocracia. É decisão sobre risco.</h2>
        <p>O responsável confirma se o conteúdo pode ser usado, se precisa ajuste ou se deve ser bloqueado antes de sair do escritório.</p>
      </div>
      <div class="human-review-checks">
        <article>
          <v-icon>mdi-account-lock-outline</v-icon>
          <strong>Dados e sigilo</strong>
          <p>Verifica cliente, processo, CPF/CNPJ, estratégia, segredo ou informação confidencial.</p>
        </article>
        <article>
          <v-icon>mdi-scale-balance</v-icon>
          <strong>Fundamento jurídico</strong>
          <p>Confere citações, jurisprudência, leis, tese e risco de informação incorreta.</p>
        </article>
        <article>
          <v-icon>mdi-send-check-outline</v-icon>
          <strong>Uso externo</strong>
          <p>Decide se pode enviar para cliente, contraparte, assinatura, tribunal ou terceiro.</p>
        </article>
        <article>
          <v-icon>mdi-file-document-check-outline</v-icon>
          <strong>Evidência</strong>
          <p>Registra responsável, motivo da decisão, ressalvas e versão revisada para auditoria.</p>
        </article>
      </div>
    </section>

    <section class="review-queue-panel">
      <div class="review-queue-header">
        <div>
          <span>Fila de revisão</span>
          <h2>{{ pendingRequests.length }} {{ pendingRequests.length === 1 ? 'documento pendente' : 'documentos pendentes' }}</h2>
        </div>
        <p>Priorize documentos com cliente, dados sensíveis, peça processual, contrato externo ou uso público.</p>
      </div>

      <div v-if="pendingRequests.length" class="review-queue-list">
        <article v-for="item in pendingRequests" :key="item.id" class="review-queue-item">
          <div class="review-queue-main">
            <div class="review-document-icon">
              <v-icon>mdi-file-document-edit-outline</v-icon>
            </div>
            <div class="review-document-copy">
              <div class="review-document-topline">
                <strong>{{ documentReference(item) }}</strong>
                <span>{{ item.use_case_type || 'Documento' }}</span>
              </div>
              <p>{{ item.business_purpose || item.data_categories || 'Documento para validação' }}</p>
              <div class="review-reason-tags">
                <span v-for="reason in reviewReasonTags(item)" :key="reason">{{ reason }}</span>
              </div>
              <div class="review-queue-facts">
                <div>
                  <span>Contexto</span>
                  <strong>{{ requestContext(item) }}</strong>
                </div>
                <div>
                  <span>Risco</span>
                  <strong class="review-status-pill" :class="`review-status-pill--risk-${item.risk_level || 'not_classified'}`">{{ riskLabel(item.risk_level) }}</strong>
                  <small>{{ confidentialityLabel(item.confidentiality_level) }}</small>
                </div>
                <div>
                  <span>Status</span>
                  <strong class="review-status-pill" :class="`review-status-pill--${item.status}`">{{ statusLabel(item.status) }}</strong>
                </div>
                <div>
                  <span>Revisor</span>
                  <strong>{{ decisionActorLabel(item) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="review-queue-actions">
            <button type="button" class="review-comment-count" @click="openDetails(item)">
              <v-icon small>mdi-comment-text-outline</v-icon>
              <span>{{ commentsCount(item.id) }}</span>
            </button>
            <v-btn small outlined color="primary" class="review-action-button" @click="openDetails(item)">Abrir</v-btn>
            <v-btn v-if="['draft', 'rejected'].includes(item.status)" small color="primary" @click="submitRequest(item)">Enviar</v-btn>
            <v-menu v-if="['submitted', 'in_review'].includes(item.status)" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small color="primary" class="review-action-button review-action-button--primary" v-bind="attrs" v-on="on">Decidir</v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="role in approvalRoles" :key="role.value" @click="decide(item, role.value, 'approve')">
                  <v-list-item-title>Liberar como {{ role.text }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="decide(item, 'juridico', 'reject')">
                  <v-list-item-title>Bloquear uso</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn v-if="item.status === 'approved'" small text color="primary" @click="generateEvidence(item)">Evidência</v-btn>
          </div>
        </article>
      </div>

      <div v-else-if="!loading" class="review-queue-empty">
        <v-icon>mdi-check-circle-outline</v-icon>
        <strong>Nenhuma revisão pendente</strong>
        <span>Documentos simples podem seguir direto pela validação.</span>
      </div>

      <section v-if="completedRequests.length" class="review-history-section">
        <div class="review-history-header">
          <div>
            <span>Histórico</span>
            <h3>{{ completedRequests.length }} {{ completedRequests.length === 1 ? 'decisão registrada' : 'decisões registradas' }}</h3>
          </div>
          <p>Aprovadas e bloqueadas continuam disponíveis com responsável, status e motivo.</p>
        </div>

        <div class="review-queue-list review-queue-list--history">
          <article v-for="item in completedRequests" :key="item.id" class="review-queue-item review-queue-item--history">
            <div class="review-queue-main">
              <div class="review-document-icon" :class="`review-document-icon--${item.status}`">
                <v-icon>{{ item.status === 'approved' ? 'mdi-file-check-outline' : 'mdi-file-cancel-outline' }}</v-icon>
              </div>
              <div class="review-document-copy">
                <div class="review-document-topline">
                  <strong>{{ documentReference(item) }}</strong>
                  <span>{{ item.use_case_type || 'Documento' }}</span>
                </div>
                <p>{{ decisionReasonLabel(item) }}</p>
                <div class="review-reason-tags">
                  <span v-for="reason in reviewReasonTags(item)" :key="reason">{{ reason }}</span>
                </div>
                <div class="review-queue-facts review-queue-facts--history">
                  <div>
                    <span>Contexto</span>
                    <strong>{{ requestContext(item) }}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong class="review-status-pill" :class="`review-status-pill--${item.status}`">{{ statusLabel(item.status) }}</strong>
                    <small>{{ decisionDateLabel(item) }}</small>
                  </div>
                  <div>
                    <span>Responsável</span>
                    <strong>{{ decisionActorLabel(item) }}</strong>
                  </div>
                  <div>
                    <span>Motivo</span>
                    <strong>{{ decisionReasonLabel(item) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div class="review-queue-actions">
              <button type="button" class="review-comment-count" @click="openDetails(item)">
                <v-icon small>mdi-comment-text-outline</v-icon>
                <span>{{ commentsCount(item.id) }}</span>
              </button>
              <v-btn small text color="primary" @click="openDetails(item)">Abrir</v-btn>
              <v-btn small text color="primary" @click="generateEvidence(item)">Evidência</v-btn>
            </div>
          </article>
        </div>
      </section>
    </section>

    <v-dialog v-model="dialog" max-width="760">
      <v-card>
        <v-card-title>Enviar para revisão humana</v-card-title>
        <v-card-text class="human-review-dialog-body">
          <v-alert v-if="dialogError" type="error" dense text>{{ dialogError }}</v-alert>

          <div class="form-grid review-dialog-grid">
            <v-select v-model="form.use_case_type" :items="useCaseOptions" label="Tipo de documento" outlined dense hide-details />
            <v-select v-model="form.confidentiality_level" :items="confidentialityOptions" label="Confidencialidade" outlined dense hide-details />
          </div>

          <v-textarea v-model="form.business_purpose" label="Resumo do documento ou motivo da revisão" outlined dense rows="3" hide-details />

          <div class="document-source-switch">
            <v-btn-toggle v-model="inputMode" mandatory dense>
              <v-btn value="file">
                <v-icon left small>mdi-file-upload-outline</v-icon>
                Arquivo
              </v-btn>
              <v-btn value="text">
                <v-icon left small>mdi-text-box-edit-outline</v-icon>
                Texto
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-file-input
            v-if="inputMode === 'file'"
            v-model="documentFile"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            label="Anexar PDF, DOCX ou TXT"
            outlined
            dense
            show-size
            hide-details
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
          />

          <v-textarea
            v-else
            v-model="form.document_text"
            label="Cole aqui o documento"
            outlined
            dense
            rows="8"
            counter
            hide-details
            no-resize
          />

          <div class="approval-risk-grid">
            <button
              v-for="option in quickRiskOptions"
              :key="option.value"
              type="button"
              class="quick-risk-option"
              :class="{ 'quick-risk-option--active': form.quick_risk === option.value }"
              @click="applyQuickRisk(option.value)"
            >
              <v-icon>{{ option.icon }}</v-icon>
              <strong>{{ option.text }}</strong>
              <span>{{ option.description }}</span>
            </button>
          </div>

          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header>Contexto opcional</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="form-grid review-dialog-grid">
                  <v-text-field v-model="form.client_name" label="Cliente" outlined dense />
                  <v-text-field v-model="form.matter_reference" label="Caso/processo/contrato" outlined dense />
                </div>
                <v-textarea v-model="form.data_categories" label="Riscos, dados sensíveis ou observações" outlined dense rows="2" />
                <div class="form-grid review-dialog-grid">
                  <v-select
                    v-model="form.human_reviewer"
                    :items="reviewerOptions"
                    :loading="reviewersLoading"
                    label="Responsável pela revisão"
                    outlined
                    dense
                    no-data-text="Nenhum funcionário ativo encontrado"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Enviar para revisão</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" fullscreen scrollable transition="dialog-bottom-transition">
      <v-card v-if="selectedRequest" class="review-workspace">
        <header class="review-workspace-header">
          <div>
            <span>Revisão humana</span>
            <h2>{{ selectedRequest.use_case_type || 'Documento jurídico' }}</h2>
            <p>{{ documentReference(selectedRequest) }}</p>
          </div>
          <div class="review-workspace-actions">
            <v-btn icon @click="detailsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </header>

        <div class="review-workspace-body">
          <aside class="review-context-panel">
            <h3>Contexto</h3>
            <div class="review-context-item">
              <span>Cliente/caso</span>
              <strong>{{ requestContext(selectedRequest) }}</strong>
            </div>
            <div class="review-context-item">
              <span>Risco</span>
              <v-chip small class="review-risk-chip" :color="riskColor(selectedRequest.risk_level)" text-color="white">
                {{ riskLabel(selectedRequest.risk_level) }}
              </v-chip>
            </div>
            <div class="review-context-item">
              <span>Confidencialidade</span>
              <strong>{{ confidentialityLabel(selectedRequest.confidentiality_level) }}</strong>
            </div>
            <div class="review-context-item">
              <span>Responsável</span>
              <strong>{{ selectedRequest.human_reviewer || 'Não definido' }}</strong>
            </div>
            <div class="review-context-item">
              <span>Resumo</span>
              <p>{{ selectedRequest.business_purpose || 'Sem resumo informado.' }}</p>
            </div>
            <div class="review-context-item">
              <span>Observações</span>
              <p>{{ selectedRequest.data_categories || 'Sem observações.' }}</p>
            </div>

            <section v-if="selectedValidation" class="review-validation-return">
              <div class="review-validation-header">
                <span>Validação automatizada</span>
                <strong>{{ validationDecisionLabel }}</strong>
              </div>
              <p v-if="validationSummary">{{ validationSummary }}</p>

              <div v-if="selectedValidation.ai_review && selectedValidation.ai_review.review" class="review-validation-grid">
                <article>
                  <span>Riscos</span>
                  <ul>
                    <li v-for="risk in selectedValidation.ai_review.review.main_risks || []" :key="risk">{{ risk }}</li>
                  </ul>
                </article>
                <article>
                  <span>Ações exigidas</span>
                  <ul>
                    <li v-for="action in selectedValidation.ai_review.review.required_actions || []" :key="action">{{ action }}</li>
                  </ul>
                </article>
              </div>

              <div v-if="selectedValidation.checks && selectedValidation.checks.length" class="review-validation-checks">
                <button
                  v-for="check in selectedValidation.checks"
                  :key="check.name"
                  type="button"
                  :class="`check-${check.status}`"
                >
                  <v-icon small>{{ check.status === 'passed' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                  <span>{{ check.name }}</span>
                </button>
              </div>
            </section>

            <div v-if="['submitted', 'in_review'].includes(selectedRequest.status)" class="review-decision-box">
              <h3>Decisão</h3>
              <v-select v-model="reviewerRole" :items="approvalRoles" label="Responsável" outlined dense hide-details />
              <v-textarea v-model="decisionReason" label="Motivo ou ressalvas" outlined dense rows="3" hide-details />
              <div class="review-decision-actions">
                <v-btn color="primary" small @click="decideFromDetails('approve')">Liberar</v-btn>
                <v-btn outlined color="error" small @click="decideFromDetails('reject')">Bloquear</v-btn>
              </div>
            </div>
          </aside>

          <main class="document-review-viewer">
            <div class="viewer-toolbar">
              <div>
                <strong>Visualizador do documento</strong>
                <span>{{ viewerModeLabel }}</span>
              </div>
              <div class="viewer-tools">
                <v-btn icon small @click="changeZoom(-10)">
                  <v-icon small>mdi-minus</v-icon>
                </v-btn>
                <span>{{ viewerZoom }}%</span>
                <v-btn icon small @click="changeZoom(10)">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
            </div>

            <v-alert dense text type="info" class="viewer-hint">
              Clique em uma região do documento para adicionar um comentário.
            </v-alert>

            <div ref="viewerScroll" class="viewer-scroll">
              <template v-if="documentPages.length">
                <section
                  v-for="(page, pageIndex) in documentPages"
                  :key="pageIndex"
                  class="document-page"
                  :data-page-index="pageIndex"
                  :style="{ transform: `scale(${viewerZoom / 100})` }"
                  @click="openCommentComposer($event, pageIndex)"
                >
                  <div class="document-page-label">Página {{ pageIndex + 1 }}</div>
                  <pre>{{ page }}</pre>
                  <div
                    v-if="pendingComment && pendingComment.page === pageIndex"
                    class="document-comment-composer"
                    :style="{ left: `${pendingComment.x}%`, top: `${pendingComment.y}%` }"
                    @click.stop
                  >
                    <textarea
                      v-model="pendingCommentText"
                      rows="4"
                      placeholder="Escreva o comentário"
                      @click.stop
                      @mousedown.stop
                      @keydown.esc="cancelCommentComposer"
                    />
                    <div>
                      <button type="button" @mousedown.stop @click.stop="cancelCommentComposer">Cancelar</button>
                      <button type="button" class="primary" @mousedown.stop @click.stop="savePendingComment">Salvar</button>
                    </div>
                  </div>
                  <button
                    v-for="comment in commentsForPage(pageIndex)"
                    :key="comment.id"
                    type="button"
                    class="document-comment-marker"
                    :class="{ active: focusedCommentId === comment.id }"
                    :data-comment-id="comment.id"
                    :style="{ left: `${comment.x}%`, top: `${comment.y}%` }"
                    @click.stop="goToComment(comment)"
                  >
                    {{ comment.number }}
                  </button>
                </section>
              </template>
              <div v-else class="document-empty-state">
                <v-icon>mdi-file-alert-outline</v-icon>
                <strong>Documento não disponível</strong>
                <p>Esta revisão foi criada antes do anexo obrigatório ou sem texto extraído.</p>
              </div>
            </div>
          </main>

          <aside class="review-comments-panel">
            <h3>Comentários</h3>
            <p class="comment-helper">Clique no documento para criar um apontamento na posição desejada.</p>

            <div class="review-comments-list">
              <article
                v-for="comment in selectedComments"
                :key="comment.id"
                :class="{ active: focusedCommentId === comment.id }"
                role="button"
                tabindex="0"
                @click="goToComment(comment)"
                @keydown.enter.prevent="goToComment(comment)"
                @keydown.space.prevent="goToComment(comment)"
              >
                <span>#{{ comment.number }} · Página {{ comment.page + 1 }}</span>
                <p>{{ comment.text }}</p>
                <v-btn x-small text color="error" @click.stop="removeComment(comment.id)">Remover</v-btn>
              </article>
              <div v-if="!selectedComments.length" class="comments-empty">
                Nenhum apontamento registrado.
              </div>
            </div>
          </aside>
        </div>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

const quickRiskDefaults = {
  internal: {
    quick_risk: 'internal',
    risk_level: 'low',
    confidentiality_level: 'internal',
    uses_personal_data: false,
    output_review_required: true,
    data_categories: 'Sem dados sensíveis ou documentos de cliente.'
  },
  client_data: {
    quick_risk: 'client_data',
    risk_level: 'medium',
    confidentiality_level: 'confidential',
    uses_personal_data: true,
    output_review_required: true,
    data_categories: 'Dados ou documentos de cliente serão usados.'
  },
  external: {
    quick_risk: 'external',
    risk_level: 'high',
    confidentiality_level: 'confidential',
    uses_personal_data: true,
    output_review_required: true,
    data_categories: 'Resultado será usado com cliente, contraparte, tribunal ou terceiro.'
  }
}

const emptyForm = () => ({
  ai_tool_id: null,
  tool_name: 'Documento para validação',
  vendor_name: 'Não informado',
  department: '',
  business_purpose: '',
  data_categories: '',
  document_text: '',
  legal_basis: 'Não definido',
  risk_level: 'medium',
  uses_personal_data: true,
  client_name: '',
  matter_reference: '',
  use_case_type: '',
  confidentiality_level: 'confidential',
  expected_benefit: '',
  output_review_required: true,
  human_reviewer: '',
  controls: 'Revisar o resultado antes de uso externo; validar citações e fundamentos; registrar evidência da aprovação.',
  quick_risk: 'client_data'
})

export default {
  name: 'RequestsPage',
  data () {
    return {
      requests: [],
      employees: [],
      dialog: false,
      form: emptyForm(),
      inputMode: 'file',
      documentFile: null,
      reviewSourceDocumentId: null,
      detailsDialog: false,
      selectedRequest: null,
      reviewerRole: 'juridico',
      decisionReason: '',
      pendingComment: null,
      pendingCommentText: '',
      focusedCommentId: null,
      viewerZoom: 100,
      commentsByRequest: {},
      validationsByRequest: {},
      loading: false,
      saving: false,
      reviewersLoading: false,
      error: '',
      dialogError: '',
      approvalRoles: [
        { text: 'Jurídico', value: 'juridico' },
        { text: 'DPO', value: 'dpo' },
        { text: 'Segurança', value: 'seguranca' }
      ],
      quickRiskOptions: [
        { value: 'internal', icon: 'mdi-office-building-outline', text: 'Uso interno', description: 'Sem cliente, dado sensível ou envio externo.' },
        { value: 'client_data', icon: 'mdi-account-lock-outline', text: 'Tem dados de cliente', description: 'Documento, caso, contrato ou dado pessoal envolvido.' },
        { value: 'external', icon: 'mdi-send-check-outline', text: 'Vai para fora', description: 'Será usado com cliente, contraparte, tribunal ou terceiro.' }
      ],
      useCaseOptions: ['Documento', 'Minuta', 'Petição', 'Minuta contratual', 'Notificação extrajudicial', 'Parecer interno', 'E-mail para cliente', 'Cláusula contratual', 'Resumo jurídico', 'Pesquisa jurídica', 'Resumo de documentos', 'Revisão contratual', 'Minuta de petição', 'Due diligence', 'Atendimento interno', 'Outro'],
      confidentialityOptions: [
        { text: 'Público', value: 'public' },
        { text: 'Interno', value: 'internal' },
        { text: 'Confidencial', value: 'confidential' },
        { text: 'Restrito', value: 'restricted' }
      ]
    }
  },
  computed: {
    pendingCount () {
      return this.requests.filter(item => ['submitted', 'in_review'].includes(item.status)).length
    },
    pendingRequests () {
      return this.requests.filter(item => ['submitted', 'in_review'].includes(item.status))
    },
    completedRequests () {
      return this.requests.filter(item => ['approved', 'rejected'].includes(item.status))
    },
    reviewerOptions () {
      return this.employees
        .filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
        .map(employee => ({
          text: `${employee.name || employee.email} · ${employee.role_name || 'Sem papel'}`,
          value: this.reviewerValue(employee)
        }))
    },
    documentPages () {
      const text = this.selectedRequest ? String(this.selectedRequest.document_text || '').trim() : ''
      if (!text) return []

      const paragraphs = text.split(/\n{2,}/)
      const pages = []
      let current = ''

      paragraphs.forEach((paragraph) => {
        const next = current ? `${current}\n\n${paragraph}` : paragraph
        if (next.length > 2600 && current) {
          pages.push(current)
          current = paragraph
        } else {
          current = next
        }
      })

      if (current) {
        pages.push(current)
      }

      return pages
    },
    selectedComments () {
      if (!this.selectedRequest) return []
      return this.commentsByRequest[this.selectedRequest.id] || []
    },
    viewerModeLabel () {
      if (!this.selectedRequest) return ''
      const type = this.selectedRequest.source_file_type || 'texto'
      return type === 'pdf' ? 'PDF extraído para revisão' : `${String(type).toUpperCase()} em modo revisão`
    },
    selectedValidation () {
      if (!this.selectedRequest) return null
      return this.validationsByRequest[this.selectedRequest.id] || null
    },
    validationDecisionLabel () {
      if (!this.selectedValidation || !this.selectedValidation.decision) return 'Análise registrada'
      return this.selectedValidation.decision.label || this.riskLabel(this.selectedValidation.decision.risk_level)
    },
    validationSummary () {
      const review = this.selectedValidation &&
        this.selectedValidation.ai_review &&
        this.selectedValidation.ai_review.review

      if (review && review.executive_summary) return review.executive_summary
      if (this.selectedValidation && this.selectedValidation.decision) {
        return this.selectedValidation.decision.next_step || this.selectedValidation.decision.message
      }
      return ''
    }
  },
  created () {
    this.load()
  },
  watch: {
    '$route.query.document_id' (documentId) {
      if (documentId && !this.dialog) {
        this.openDialogFromDocument(documentId)
      }
    }
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const [requests, employees] = await Promise.all([
          api.get('/human-review-requests'),
          api.get('/employees')
        ])
        this.requests = this.responseRows(requests.data)
        this.employees = employees.data
        this.loadListComments()
        this.loadListValidations()
        if (this.$route.query.document_id && !this.dialog) {
          await this.openDialogFromDocument(this.$route.query.document_id)
        }
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar revisões.')
      } finally {
        this.loading = false
      }
    },
    openDetails (item) {
      this.selectedRequest = item
      this.detailsDialog = true
      this.reviewerRole = 'juridico'
      this.decisionReason = ''
      this.pendingComment = null
      this.pendingCommentText = ''
      this.focusedCommentId = null
      this.viewerZoom = 100
      this.loadStoredComments(item.id)
      this.loadStoredValidation(item.id)
    },
    responseRows (data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    openDialog () {
      this.form = emptyForm()
      this.form.human_reviewer = this.defaultReviewer()
      this.inputMode = 'file'
      this.documentFile = null
      this.reviewSourceDocumentId = null
      this.dialogError = ''
      this.dialog = true
    },
    async closeDialog () {
      this.dialog = false
      this.dialogError = ''
      this.reviewSourceDocumentId = null
      await this.clearDocumentQuery()
    },
    async clearDocumentQuery () {
      if (!this.$route.query.document_id) return

      const query = { ...this.$route.query }
      delete query.document_id

      try {
        await this.$router.replace({ name: this.$route.name, query })
      } catch (error) {
        // Avoid surfacing duplicated-navigation noise from Vue Router.
      }
    },
    async openDialogFromDocument (documentId) {
      try {
        const { data } = await api.get(`/generated-documents/${documentId}`)
        this.form = {
          ...emptyForm(),
          business_purpose: `${data.document_type || 'Documento'} enviado para revisão humana.`,
          document_text: data.document_text || '',
          generated_document_id: data.id,
          client_name: data.client_name || '',
          matter_reference: data.matter_reference || '',
          use_case_type: data.document_type || 'Documento jurídico',
          confidentiality_level: data.client_name ? 'confidential' : 'internal',
          expected_benefit: 'Liberar, ajustar ou bloquear documento antes do uso.',
          human_reviewer: this.defaultReviewer(),
          controls: 'Revisar conteúdo, fontes, placeholders, dados sensíveis e comentários antes de liberar.',
          quick_risk: data.client_name ? 'client_data' : 'internal'
        }
        this.inputMode = 'text'
        this.documentFile = null
        this.reviewSourceDocumentId = documentId
        this.dialogError = ''
        this.dialog = true
      } catch (error) {
        this.error = 'Não foi possível carregar o documento para revisão.'
      }
    },
    reviewerValue (employee) {
      const name = employee.name || employee.email
      return employee.email ? `${name} <${employee.email}>` : name
    },
    defaultReviewer () {
      const active = this.employees.filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
      const preferred = active.find(employee => ['legal', 'admin', 'tenant_owner', 'security_dpo'].includes(employee.role_slug))
      const reviewer = preferred || active[0]
      return reviewer ? this.reviewerValue(reviewer) : ''
    },
    reviewerLabel (item) {
      return item.human_reviewer || 'Não definido'
    },
    decisionActorLabel (item) {
      const decidedStep = this.decidedApprovalStep(item)
      if (!decidedStep) {
        return this.reviewerLabel(item)
      }

      const approver = decidedStep.approver || {}
      const name = approver.name || approver.email || item.human_reviewer || 'Responsável'
      return `Decidido por ${name}`
    },
    decidedApprovalStep (item) {
      const approvals = Array.isArray(item.approvals) ? item.approvals : []
      return approvals
        .filter(step => ['approved', 'rejected'].includes(step.status))
        .sort((a, b) => new Date(b.decided_at || b.updated_at || 0) - new Date(a.decided_at || a.updated_at || 0))[0] || null
    },
    decisionReasonLabel (item) {
      const step = this.decidedApprovalStep(item)
      return (step && step.decision_reason) || item.review_checklist || item.controls || 'Decisão registrada sem motivo informado.'
    },
    decisionDateLabel (item) {
      const step = this.decidedApprovalStep(item)
      const value = (step && (step.decided_at || step.updated_at)) || item.completed_at || item.updated_at
      if (!value) return 'Sem data'

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(value))
    },
    commentsCount (requestId) {
      const comments = this.commentsByRequest[requestId] || []
      return comments.length
    },
    loadListComments () {
      this.requests.forEach(item => {
        this.loadStoredComments(item.id)
      })
    },
    reviewValidationKey (requestId) {
      return `complyops:review-validation:${requestId}`
    },
    loadStoredValidation (requestId) {
      try {
        const stored = window.localStorage.getItem(this.reviewValidationKey(requestId))
        this.$set(this.validationsByRequest, requestId, stored ? JSON.parse(stored) : null)
      } catch (error) {
        this.$set(this.validationsByRequest, requestId, null)
      }
    },
    loadListValidations () {
      this.requests.forEach(item => {
        this.loadStoredValidation(item.id)
      })
    },
    applyQuickRisk (risk) {
      this.form = { ...this.form, ...quickRiskDefaults[risk] }
    },
    async save () {
      this.saving = true
      this.error = ''
      this.dialogError = ''
      try {
        if (this.inputMode === 'file' && !this.documentFile) {
          this.dialogError = 'Anexe o documento que será revisado.'
          return
        }

        if (this.inputMode === 'text' && String(this.form.document_text || '').trim().length < 40) {
          this.dialogError = 'Cole o documento com ao menos 40 caracteres.'
          return
        }

        const payload = {
          ...this.form,
          tool_name: this.form.tool_name || 'Documento para validação',
          vendor_name: this.form.vendor_name || 'Não informado',
          use_case_type: this.form.use_case_type || 'Documento jurídico',
          business_purpose: this.form.business_purpose || 'Documento enviado para revisão humana.',
          data_categories: this.form.data_categories || 'Riscos e dados sensíveis serão avaliados pelo responsável humano.',
          submit_now: true
        }
        delete payload.quick_risk

        const formData = new FormData()
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined) {
            formData.append(key, payload[key])
          }
        })

        if (this.inputMode === 'file') {
          formData.append('document_file', this.documentFile)
        }

        await api.post('/human-review-requests', formData)
        if (this.reviewSourceDocumentId) {
          await api.put(`/generated-documents/${this.reviewSourceDocumentId}`, { status: 'in_review' })
          this.reviewSourceDocumentId = null
        }
        this.dialog = false
        await this.clearDocumentQuery()
        await this.load()
      } catch (error) {
        this.dialogError = error.response && error.response.data ? error.response.data.message : 'Não foi possível enviar para revisão.'
      } finally {
        this.saving = false
      }
    },
    async submitRequest (item) {
      await api.post(`/human-review-requests/${item.id}/submit`)
      await this.load()
    },
    async decide (item, role, decision) {
      await api.post(`/human-review-requests/${item.id}/${decision}`, {
        approver_role: role,
        decision_reason: decision === 'approve' ? `Aprovado por ${this.roleLabel(role)}` : 'Reprovado na avaliação'
      })
      await this.load()
    },
    async decideFromDetails (decision) {
      if (!this.selectedRequest) return

      await api.post(`/human-review-requests/${this.selectedRequest.id}/${decision}`, {
        approver_role: this.reviewerRole,
        decision_reason: this.decisionReason || (decision === 'approve' ? `Liberado por ${this.roleLabel(this.reviewerRole)}` : 'Bloqueado na revisão humana')
      })
      this.detailsDialog = false
      await this.load()
    },
    async generateEvidence (item) {
      await api.post('/evidences', {
        entity_type: 'human_document_review',
        entity_id: item.id,
        name: `Dossiê de aprovação - ${item.client_name || this.documentReference(item)}`
      })
      this.$router.push({ name: 'evidences' })
    },
    requestContext (item) {
      const parts = [item.client_name, item.matter_reference].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Uso sem cliente/caso informado'
    },
    documentReference (item) {
      const linkedDocument = item.generatedDocument || item.generated_document
      if (linkedDocument && linkedDocument.title) {
        return linkedDocument.title
      }

      if (item.source_file_name) {
        return item.source_file_name
      }

      if (item.document_title) {
        return item.document_title
      }

      return item.generated_document_id ? `Documento #${item.generated_document_id}` : (item.document_text ? 'Texto colado na revisão' : 'Documento não anexado')
    },
    reviewReasonTags (item) {
      const reasons = []
      const text = `${item.business_purpose || ''} ${item.data_categories || ''} ${item.use_case_type || ''}`.toLowerCase()

      if (['confidential', 'restricted'].includes(item.confidentiality_level) || item.uses_personal_data) {
        reasons.push('Dados/sigilo')
      }

      if (/(cliente|contraparte|tribunal|terceiro|externo|protocolo|assinatura)/i.test(text)) {
        reasons.push('Uso externo')
      }

      if (/(petição|parecer|contrato|minuta|jurisprudência|lei|processo)/i.test(text)) {
        reasons.push('Risco jurídico')
      }

      if (['high', 'critical'].includes(item.risk_level)) {
        reasons.push('Risco alto')
      }

      return reasons.length ? reasons.slice(0, 3) : ['Exceção de política']
    },
    changeZoom (amount) {
      this.viewerZoom = Math.max(70, Math.min(140, this.viewerZoom + amount))
    },
    commentsKey (requestId) {
      return `complyops:review-comments:${requestId}`
    },
    loadStoredComments (requestId) {
      try {
        const stored = window.localStorage.getItem(this.commentsKey(requestId))
        this.$set(this.commentsByRequest, requestId, stored ? JSON.parse(stored) : [])
      } catch (error) {
        this.$set(this.commentsByRequest, requestId, [])
      }
    },
    saveStoredComments () {
      if (!this.selectedRequest) return
      window.localStorage.setItem(this.commentsKey(this.selectedRequest.id), JSON.stringify(this.selectedComments))
    },
    openCommentComposer (event, pageIndex) {
      if (!this.selectedRequest) return
      if (event.target && event.target.closest && event.target.closest('.document-comment-composer')) return

      const rect = event.currentTarget.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) * 100
      const y = ((event.clientY - rect.top) / rect.height) * 100

      this.pendingComment = {
        page: pageIndex,
        x: Math.max(4, Math.min(96, x)),
        y: Math.max(4, Math.min(96, y))
      }
      this.pendingCommentText = ''
    },
    cancelCommentComposer (event) {
      if (event && event.stopPropagation) {
        event.stopPropagation()
      }
      this.pendingComment = null
      this.pendingCommentText = ''
    },
    savePendingComment (event) {
      if (event && event.stopPropagation) {
        event.stopPropagation()
      }
      if (!this.selectedRequest || !this.pendingComment || !this.pendingCommentText.trim()) return

      const comments = [...this.selectedComments]
      const comment = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        number: comments.length + 1,
        page: this.pendingComment.page,
        x: this.pendingComment.x,
        y: this.pendingComment.y,
        text: this.pendingCommentText.trim(),
        created_at: new Date().toISOString()
      }

      comments.push(comment)
      this.$set(this.commentsByRequest, this.selectedRequest.id, comments)
      this.focusedCommentId = comment.id
      this.pendingComment = null
      this.pendingCommentText = ''
      this.saveStoredComments()
    },
    commentsForPage (pageIndex) {
      return this.selectedComments.filter(comment => comment.page === pageIndex)
    },
    focusComment (commentId) {
      this.focusedCommentId = commentId
    },
    goToComment (comment) {
      if (!comment) return

      this.focusedCommentId = comment.id
      this.$nextTick(() => {
        this.scrollViewerToComment(comment)
        window.setTimeout(() => this.scrollViewerToComment(comment), 120)
      })
    },
    scrollViewerToComment (comment) {
      const container = this.$refs.viewerScroll || document.querySelector('.review-workspace .viewer-scroll')
      if (!container) return

      const page = document.querySelector(`.review-workspace .document-page[data-page-index="${comment.page}"]`)
      const marker = document.querySelector(`.review-workspace .document-comment-marker[data-comment-id="${comment.id}"]`)

      if (page) {
        const targetTop = page.offsetTop + (page.offsetHeight * (Number(comment.y) || 0) / 100) - (container.clientHeight / 2)
        const targetLeft = page.offsetLeft + (page.offsetWidth * (Number(comment.x) || 0) / 100) - (container.clientWidth / 2)
        container.scrollTop = Math.max(0, Math.min(targetTop, container.scrollHeight - container.clientHeight))
        container.scrollLeft = Math.max(0, Math.min(targetLeft, container.scrollWidth - container.clientWidth))
        return
      }

      if (!marker) return
      marker.scrollIntoView({ block: 'center', inline: 'center' })
    },
    removeComment (commentId) {
      if (!this.selectedRequest) return
      const comments = this.selectedComments
        .filter(comment => comment.id !== commentId)
        .map((comment, index) => ({ ...comment, number: index + 1 }))
      this.$set(this.commentsByRequest, this.selectedRequest.id, comments)
      this.saveStoredComments()
    },
    statusLabel (status) {
      return { draft: 'Rascunho', submitted: 'Aguardando', in_review: 'Em revisão', approved: 'Aprovado', rejected: 'Bloqueado' }[status] || status
    },
    statusColor (status) {
      return { draft: 'grey', submitted: 'warning', in_review: 'warning', approved: 'success', rejected: 'error' }[status] || 'grey'
    },
    riskLabel (risk) {
      return { not_classified: 'Não classificado', low: 'Baixo', medium: 'Médio', high: 'Alto', critical: 'Crítico' }[risk] || risk || '-'
    },
    riskColor (risk) {
      return { not_classified: 'grey', low: 'success', medium: 'primary', high: 'warning', critical: 'error' }[risk] || 'grey'
    },
    roleLabel (role) {
      return { juridico: 'Jurídico', dpo: 'DPO', seguranca: 'Segurança' }[role] || role
    },
    confidentialityLabel (level) {
      return { public: 'Público', internal: 'Interno', confidential: 'Confidencial', restricted: 'Restrito' }[level] || level || '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.review-queue-panel {
  padding: 28px;
  border: 1px solid #dfe4e7;
  background: #ffffff;
}

.review-queue-header {
  display: grid;
  grid-template-columns: minmax(280px, auto) minmax(320px, 1fr);
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e4eaed;
}

.review-queue-header span {
  display: block;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.review-queue-header h2 {
  margin: 7px 0 0;
  color: #101918;
  font-size: 30px;
  line-height: 1.1;
}

.review-queue-header p {
  max-width: 720px;
  margin: 6px 0 0;
  color: #647280;
  font-size: 15px;
  line-height: 1.45;
  text-align: right;
}

.review-queue-list {
  display: grid;
  gap: 10px;
}

.review-queue-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 128px;
  gap: 14px;
  align-items: center;
  min-height: 0;
  padding: 14px 16px;
  border: 1px solid #e2e8ec;
  border-left: 4px solid #0f4efb;
  background: #ffffff;
  box-shadow: 0 10px 28px -26px rgba(15, 78, 251, .55);
}

.review-queue-main {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
}

.review-document-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d9e5ff;
  background: #f4f7ff;
  color: #0f4efb;
}

.review-document-icon .v-icon {
  font-size: 21px;
}

.review-document-icon .v-icon {
  color: #0f4efb;
}

.review-document-icon--approved {
  border-color: #bfead2;
  background: #effaf4;
}

.review-document-icon--approved .v-icon {
  color: #13965a;
}

.review-document-icon--rejected {
  border-color: #f0c6cc;
  background: #fff1f3;
}

.review-document-icon--rejected .v-icon {
  color: #bd233d;
}

.review-document-copy {
  min-width: 0;
}

.review-document-topline {
  display: flex;
  gap: 10px;
  align-items: baseline;
  min-width: 0;
}

.review-document-topline strong {
  min-width: 0;
  color: #101918;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-document-topline span {
  flex: 0 0 auto;
  color: #647280;
  font-size: 12px;
  font-weight: 850;
}

.review-document-copy p {
  display: -webkit-box;
  margin: 5px 0 8px;
  color: #394853;
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.review-reason-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.review-reason-tags span {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 7px;
  border: 1px solid #dfe5e8;
  background: #ffffff;
  color: #3e4b54;
  font-size: 10px;
  font-weight: 850;
  line-height: 1;
}

.review-queue-facts {
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) minmax(96px, .72fr) minmax(104px, .72fr) minmax(130px, 1fr);
  gap: 10px;
  align-items: start;
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px solid #edf1f3;
}

.review-queue-facts > div {
  min-width: 0;
}

.review-queue-facts span {
  display: block;
  margin-bottom: 3px;
  color: #687783;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}

.review-queue-facts strong:not(.review-status-pill) {
  display: block;
  color: #101918;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-queue-facts small {
  display: block;
  margin-top: 3px;
  color: #647280;
  font-size: 10px;
  line-height: 1.2;
}

.review-queue-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  align-items: start;
  padding: 12px;
  border: 1px solid #e7edf0;
  background: #fbfcfd;
}

.review-meta-block {
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.review-meta-block span {
  display: block;
  margin-bottom: 4px;
  color: #687783;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.review-meta-block strong {
  display: block;
  color: #101918;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.review-meta-block small {
  display: block;
  margin-top: 6px;
  color: #647280;
  font-size: 11px;
  line-height: 1.2;
}

.review-meta-block--compact {
  text-align: left;
}

.review-status-pill {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 100%;
  min-height: 22px;
  padding: 0 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 10px !important;
  font-weight: 900 !important;
  letter-spacing: .06em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

.review-status-pill--submitted,
.review-status-pill--in_review {
  border-color: #f1d69a;
  background: #fff6de;
  color: #8a5a00 !important;
}

.review-status-pill--approved {
  border-color: #b9e6cb;
  background: #edf9f2;
  color: #137a49 !important;
}

.review-status-pill--rejected {
  border-color: #efc0c7;
  background: #fff0f3;
  color: #a51f38 !important;
}

.review-status-pill--draft,
.review-status-pill--risk-not_classified {
  border-color: #d9e1e6;
  background: #f3f6f8;
  color: #53616c !important;
}

.review-status-pill--risk-low {
  border-color: #bee7d1;
  background: #edf9f3;
  color: #117747 !important;
}

.review-status-pill--risk-medium {
  border-color: #bfd0ff;
  background: #eef4ff;
  color: #0f4efb !important;
}

.review-status-pill--risk-high {
  border-color: #f0d39b;
  background: #fff6df;
  color: #8a5a00 !important;
}

.review-status-pill--risk-critical {
  border-color: #efbbc4;
  background: #fff0f3;
  color: #9f2139 !important;
}

.review-queue-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
  min-width: 128px;
}

.review-queue-actions .v-btn {
  width: 100% !important;
  min-width: 0 !important;
  box-shadow: none !important;
}

.review-comment-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 30px;
  width: 100%;
  border: 1px solid #d8e1e7;
  border-radius: 999px;
  background: #ffffff;
  color: #4d5c67;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.review-comment-count .v-icon {
  color: #647280;
}

.review-queue-empty {
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 180px;
  border: 1px dashed #dfe5e8;
  background: #fbfcfc;
  color: #647280;
  text-align: center;
}

.review-queue-empty .v-icon {
  color: #16a35f;
}

.review-queue-empty strong {
  color: #101918;
  font-size: 17px;
  font-weight: 900;
}

.review-history-section {
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid #e4eaed;
}

.review-history-header {
  display: grid;
  grid-template-columns: minmax(280px, auto) minmax(320px, 1fr);
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  margin-bottom: 16px;
}

.review-history-header span {
  display: block;
  color: #647280;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.review-history-header h3 {
  margin: 6px 0 0;
  color: #101918;
  font-size: 22px;
  line-height: 1.15;
}

.review-history-header p {
  max-width: 520px;
  margin: 0;
  color: #647280;
  font-size: 14px;
  line-height: 1.4;
  text-align: right;
}

.review-queue-list--history {
  gap: 10px;
}

.review-queue-item--history {
  grid-template-columns: minmax(0, 1fr) 128px;
  align-items: center;
  min-height: 0;
  background: #ffffff;
}

.review-queue-item--history .review-queue-actions {
  align-self: center;
  justify-content: flex-start;
}

.human-review-dialog-body {
  display: grid;
  gap: 16px;
  padding-top: 8px !important;
}

.review-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.document-source-switch {
  margin-top: -2px;
}

.approval-risk-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.quick-risk-option {
  min-height: 104px;
  padding: 14px;
  border: 1px solid #dfe8e4;
  background: #fff;
  color: #101918;
  text-align: left;
  display: grid;
  gap: 6px;
  cursor: pointer;
}

.quick-risk-option .v-icon {
  color: #0f4efb;
}

.quick-risk-option strong,
.quick-risk-option span {
  display: block;
}

.quick-risk-option span {
  color: #60717d;
  font-size: 12px;
  line-height: 1.35;
}

.quick-risk-option--active {
  border-color: #0f4efb;
  box-shadow: inset 0 0 0 1px #0f4efb;
}

@media (max-width: 1500px) {
  .review-queue-header,
  .review-history-header {
    grid-template-columns: 1fr;
  }

  .review-queue-header p,
  .review-history-header p {
    max-width: none;
    text-align: left;
  }

  .review-queue-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .review-dialog-grid,
  .approval-risk-grid {
    grid-template-columns: 1fr;
  }

  .review-queue-panel {
    padding: 18px;
  }

  .review-queue-header {
    display: grid;
  }

  .review-queue-header p {
    text-align: left;
  }

  .review-history-header {
    display: grid;
  }

  .review-history-header p {
    text-align: left;
  }

  .review-queue-item,
  .review-queue-item--history,
  .review-queue-facts {
    grid-template-columns: 1fr;
  }

  .review-queue-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
